import { v4 as uuid } from 'uuid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { instanceWorkflowsApi, type BusinessWorkflow, type WorkflowProjection } from '@/services/instanceWorkflows'
import { editWorkflow, type WorkflowEdit } from '../config/workflow'
import type { EditResult } from '../contracts'

type Workspace = { selected: string | null; visible: boolean; drafts: Map<string, BusinessWorkflow>; bases: Map<string, number>; error: string | null }
const workspace = (): Workspace => ({ selected: null, visible: false, drafts: new Map(), bases: new Map(), error: null })
/** Source revisions fence late responses; selection is never an activation command. */
const accepted = (previous: WorkflowProjection | undefined, next: WorkflowProjection): WorkflowProjection => {
  if (!previous) return next
  return previous.active_revision > next.active_revision || previous.revision > next.revision ? previous : next
}
export const useWorkflows = (userId: string, instanceId: string, sourceRevision?: number) => {
  const client = useQueryClient()
  const [workspaces, setWorkspaces] = useState(new Map<string, Workspace>())
  const current = useRef(workspaces)
  const [pending, setPending] = useState(new Set<string>())
  const live = useRef(true)
  const requests = useRef(new Map<string, { controller: AbortController; promise: Promise<WorkflowProjection> }>())
  const requestIds = useRef(new Map<string, string>())
  const queryKey = useMemo(() => ['instance-administration', userId, 'business-workflows', instanceId], [instanceId, userId])
  const query = useQuery({ queryKey, enabled: !!instanceId, retry: false,
    queryFn: async ({ signal }) => accepted(client.getQueryData<WorkflowProjection>(queryKey), await instanceWorkflowsApi.read(instanceId, signal)) })
  const update = useCallback((change: (value: Workspace) => Workspace) => {
    const next = new Map(current.current).set(instanceId, change(current.current.get(instanceId) ?? workspace()))
    current.current = next; setWorkspaces(next)
  }, [instanceId])
  const { refetch } = query
  useEffect(() => { if (sourceRevision !== undefined && instanceId) void refetch() }, [instanceId, refetch, sourceRevision])
  useEffect(() => {
    live.current = true
    const owned = requests.current
    return () => { live.current = false; owned.forEach(({ controller }) => controller.abort()); owned.clear() }
  }, [])
  const state = workspaces.get(instanceId) ?? workspace()
  const flows = useMemo(() => {
    const merged = new Map(query.data?.workflows.map((item) => [item.workflow_id, item]) ?? [])
    state.drafts.forEach((value, key) => merged.set(key, value))
    return [...merged.values()]
  }, [query.data?.workflows, state.drafts])
  const selected = flows.find((item) => item.workflow_id === state.selected)
  const changed = !!selected && JSON.stringify(selected) !== JSON.stringify(query.data?.workflows.find((item) => item.workflow_id === selected.workflow_id))
  const select = useCallback((id: string) => update((value) => ({ ...value, selected: id, visible: true, error: null })), [update])
  const hide = useCallback(() => update((value) => ({ ...value, visible: false })), [update])
  const discard = useCallback(() => update((value) => {
    const drafts = new Map(value.drafts)
    const bases = new Map(value.bases)
    if (value.selected) { drafts.delete(value.selected); bases.delete(value.selected) }
    return { ...value, drafts, bases, error: null }
  }), [update])
  const create = useCallback((title: string) => update((value) => {
    const flow: BusinessWorkflow = { workflow_id: uuid(), title: title.trim(), description: '', entry_step_id: null, steps: [], transitions: [] }
    return { ...value, selected: flow.workflow_id, visible: true, drafts: new Map(value.drafts).set(flow.workflow_id, flow), bases: new Map(value.bases).set(flow.workflow_id, query.data?.revision ?? 0), error: null }
  }), [query.data?.revision, update])
  const edit = useCallback((command: WorkflowEdit): EditResult => {
    try {
      const value = current.current.get(instanceId) ?? workspace()
      const flow = value.drafts.get(value.selected ?? '') ?? query.data?.workflows.find((item) => item.workflow_id === value.selected)
      if (!flow) throw new Error('请先选择流程')
      const next = editWorkflow(flow, command, query.data?.operations ?? [])
      update((previous) => ({ ...previous, drafts: new Map(previous.drafts).set(next.workflow_id, next),
        bases: new Map(previous.bases).set(next.workflow_id, previous.bases.get(next.workflow_id) ?? query.data?.revision ?? 0), error: null }))
      return { kind: 'accepted' }
    } catch (error) { const message = (error as Error).message; update((value) => ({ ...value, error: message })); return { kind: 'rejected', message } }
  }, [instanceId, query.data, update])
  const execute = useCallback((kind: 'save' | 'delete' | 'initialize' | 'activate', flow?: BusinessWorkflow): Promise<WorkflowProjection> => {
    const data = query.data
    const target = flow ?? selected
    if (!data || !target) return Promise.reject(new Error('请先选择流程'))
    const body = { expected_revision: current.current.get(instanceId)?.bases.get(target.workflow_id) ?? data.revision, workflow_id: target.workflow_id,
      ...(kind === 'activate' ? { expected_active_revision: data.active_revision } : { operation: kind, ...(kind === 'save' ? { workflow: target } : {}) }) }
    const identity = JSON.stringify([instanceId, kind, body])
    const existing = requests.current.get(identity)
    if (existing) return existing.promise
    if ([...requests.current.keys()].some((key) => JSON.parse(key)[0] === instanceId)) return Promise.reject(new Error('此角色正在保存流程'))
    const requestId = requestIds.current.get(identity) ?? uuid()
    requestIds.current.set(identity, requestId)
    const controller = new AbortController()
    setPending((previous) => new Set(previous).add(instanceId))
    const command = { ...body, request_id: requestId }
    const invoke = kind === 'activate'
      ? instanceWorkflowsApi.activate(instanceId, command as Parameters<typeof instanceWorkflowsApi.activate>[1], controller.signal)
      : instanceWorkflowsApi.save(instanceId, command as Parameters<typeof instanceWorkflowsApi.save>[1], controller.signal)
    const promise = invoke.then((result) => {
      if (!live.current) return result
      const previous = client.getQueryData<WorkflowProjection>(queryKey)
      if (accepted(previous, result) !== result) throw new Error('配置已变化，请重新读取后核对本次保存结果')
      client.setQueryData<WorkflowProjection>(queryKey, (previous) => accepted(previous, result))
      update((value) => {
        const drafts = new Map(value.drafts)
        const bases = new Map(value.bases)
        if (JSON.stringify(drafts.get(target.workflow_id)) === JSON.stringify(target)) { drafts.delete(target.workflow_id); bases.delete(target.workflow_id) }
        return { ...value, drafts, bases, error: null, ...(kind === 'initialize' ? { selected: target.workflow_id, visible: true } : {}),
          ...(kind === 'delete' && value.selected === target.workflow_id ? { selected: null } : {}) }
      })
      return result
    }).catch((error: Error) => { if (live.current) update((value) => ({ ...value, error: error.message })); throw error })
      .finally(() => { requests.current.delete(identity); if (live.current) setPending((previous) => new Set([...previous].filter((id) => id !== instanceId))) })
    requests.current.set(identity, { controller, promise })
    return promise
  }, [client, instanceId, query.data, queryKey, selected, update])
  const save = useCallback(() => execute('save'), [execute])
  const activate = useCallback(() => execute('activate'), [execute])
  const remove = useCallback(() => execute('delete'), [execute])
  const initialize = useCallback(() => execute('initialize', query.data?.initial_workflow ?? undefined), [execute, query.data?.initial_workflow])
  const reload = useCallback(async () => { const result = await refetch(); if (result.error) throw result.error }, [refetch])
  return { instanceId, data: query.data, flows, selected, visible: state.visible, changed, pending: pending.has(instanceId),
    error: state.error ?? query.error?.message ?? null, loading: query.isPending, create, select, hide, discard, edit, save, activate, remove, initialize, reload }
}
